import {
  META_PREFIX,
  FORM_KEY_PROP,
} from '@de/utils/binding-enhancer/constants';
import { COMPONENT_TRACKING_DEFAULTS } from '@de/components/Trackable/componentTrackingDefaults';
import type { CustomDimensions } from '@de/types/types';

// Flat dotted tracking prop keys on the raw `data.content` tree (before the
// binding resolver strips the `_meta.` prefix).
const NODE_TRACKING_PROP = `${META_PREFIX}tracking`; // '_meta.tracking'
const NODE_TRACKING_KIND_PROP = `${NODE_TRACKING_PROP}.kind`;
const NODE_TRACKING_FIELDNAME_PROP = `${NODE_TRACKING_PROP}.fieldName`;
const NODE_TRACKING_LEADFIELD_PROP = `${NODE_TRACKING_PROP}.leadFieldAssociated`;
const NODE_TRACKING_FIELDTYPE_PROP = `${NODE_TRACKING_PROP}.fieldType`;

type ComponentNode = {
  readonly type?: unknown;
  readonly props?: Readonly<Record<string, unknown>>;
};

const isComponentNode = (value: unknown): value is ComponentNode =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  typeof (value as ComponentNode).type === 'string';

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const firstString = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim() !== '') return value.trim();
  }
  return undefined;
};

// Effective tracking `kind`: per-instance `_meta.tracking.kind` wins over the
// flow registry (mirrors `createBoundRender.mergeTrackingMeta`). A host
// `componentTrackingDefaults` map is not consulted here; the entry field
// summary stays on the flow registry.
function resolveNodeKind(node: ComponentNode): string | undefined {
  const props = node.props ?? {};
  const trackingSlice = props[NODE_TRACKING_PROP];
  if (isPlainObject(trackingSlice) && typeof trackingSlice.kind === 'string') {
    return trackingSlice.kind;
  }
  const flatKind = props[NODE_TRACKING_KIND_PROP];
  if (typeof flatKind === 'string') return flatKind;

  const type = node.type;
  if (typeof type === 'string') {
    return COMPONENT_TRACKING_DEFAULTS[type]?.kind;
  }
  return undefined;
}

// Field name for a `kind: 'field'` node: fieldName → leadFieldAssociated →
// formKey → name, then a fieldType/type fallback so unlabeled fields still count.
function resolveFieldName(node: ComponentNode): string | undefined {
  const props = node.props ?? {};
  const trackingSlice = props[NODE_TRACKING_PROP];

  const fromSlice = isPlainObject(trackingSlice)
    ? firstString(trackingSlice.fieldName, trackingSlice.leadFieldAssociated)
    : undefined;

  const explicit = firstString(
    fromSlice,
    props[NODE_TRACKING_FIELDNAME_PROP],
    props[NODE_TRACKING_LEADFIELD_PROP],
    props[FORM_KEY_PROP],
    props.name,
  );
  if (explicit) return explicit;

  // Fallback: field type, then component type.
  const sliceFieldType = isPlainObject(trackingSlice)
    ? trackingSlice.fieldType
    : undefined;
  const type = typeof node.type === 'string' ? node.type : undefined;
  const registryFieldType = type
    ? COMPONENT_TRACKING_DEFAULTS[type]?.fieldType
    : undefined;
  const fallback = firstString(
    sliceFieldType,
    props[NODE_TRACKING_FIELDTYPE_PROP],
    registryFieldType,
    type,
  );
  return fallback ? fallback.toLowerCase() : undefined;
}

function collectFieldNames(node: unknown, acc: string[]): void {
  if (Array.isArray(node)) {
    for (const item of node) collectFieldNames(item, acc);
    return;
  }
  if (!isComponentNode(node)) return;

  if (resolveNodeKind(node) === 'field') {
    const name = resolveFieldName(node);
    if (name) acc.push(name);
  }

  const props = node.props ?? {};
  for (const value of Object.values(props)) {
    if (Array.isArray(value) || isComponentNode(value)) {
      collectFieldNames(value, acc);
    }
  }
}

// Summarize an entry step's declared fields into dimensions: `fieldCount`,
// `fieldNames` (ordered, comma-joined) and `fieldSet` (sorted, `_`-joined — a
// stable GROUP BY key). Empty when the step declares no fields. Reads the config
// tree statically, so it's correct before async data sources resolve.
export function buildEntryFieldSummary(content: unknown): CustomDimensions {
  const names: string[] = [];
  collectFieldNames(content, names);
  if (names.length === 0) return [];

  const ordered: string[] = [];
  const seen = new Set<string>();
  for (const name of names) {
    if (!seen.has(name)) {
      seen.add(name);
      ordered.push(name);
    }
  }

  const sorted = Array.from(
    new Set(ordered.map((name) => name.toLowerCase())),
  ).sort((a, b) => a.localeCompare(b));

  return [
    { key: 'fieldCount', value: String(ordered.length) },
    { key: 'fieldNames', value: ordered.join(',') },
    { key: 'fieldSet', value: sorted.join('_') },
  ];
}
